/**
 * MyFaceDr Geo-Redirect Module
 * Handles automatic region-based redirection with manual override support
 */

(function(window) {
  'use strict';

  const CONFIG = {
    manualLocationParam: 'manual_location'
  };

  /**
   * Preference Storage Module (using Shopify Cart Attributes)
   */
  const PreferenceManager = {
    async set(storeCode) {
      try {
        const response = await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            attributes: { location_preference: storeCode }
          }),
        });

        if (response.ok) {
          Logger.log(`Set location preference to: ${storeCode}`);
          return true;
        }
        return false;
      } catch (error) {
        Logger.warn('Failed to set cart attributes');
        return false;
      }
    }
  };

  /**
   * Bot Detection Module
   */
  const BotDetector = {
    patterns: [
      /bot/i, /spider/i, /crawl/i, /APIs-Google/i, /AdsBot/i, /Googlebot/i,
      /mediapartners/i, /Google Favicon/i, /FeedFetcher/i, /Google-Read-Aloud/i,
      /DuplexWeb-Google/i, /googleweblight/i, /bing/i, /yandex/i, /baidu/i,
      /duckduck/i, /yahoo/i, /ecosia/i, /ia_archiver/i, /lighthouse/i,
      /slurp/i, /curl/i, /wget/i, /Screaming Frog/i, /Semrush/i
    ],

    isBot() {
      return this.patterns.some(pattern => pattern.test(navigator.userAgent));
    }
  };

  /**
   * URL Parameter Manager
   */
  const URLManager = {
    hasManualLocationParam() {
      const params = new URLSearchParams(window.location.search);
      return params.has(CONFIG.manualLocationParam);
    },

    addManualLocationParam(url) {
      const urlObj = new URL(url);
      urlObj.searchParams.set(CONFIG.manualLocationParam, 'true');
      return urlObj.toString();
    },

    removeManualLocationParam() {
      const params = new URLSearchParams(window.location.search);
      params.delete(CONFIG.manualLocationParam);
      const search = params.toString();
      const newUrl = window.location.pathname + (search ? '?' + search : '');

      if (window.history?.replaceState) {
        window.history.replaceState({}, document.title, newUrl);
      }
    }
  };

  /**
   * Location Detection Module
   */
  const LocationDetector = {
         async getUserCountry(fallbackCountry) {

      // Try Shopify's geolocation endpoint
      try {
        const response = await fetch(`${window.location.origin}/browsing_context_suggestions.json`);
        const data = await response.json();
        const countryCode = data?.detected_values?.country?.handle;
        if (countryCode) {
          Logger.log(`Geolocation via Shopify successful: ${countryCode}`);
          return countryCode;
        }
      } catch (error) {
        Logger.warn('Geolocation service failed, using fallback');
      }

      // Final fallback
      return fallbackCountry;
    }
  };

  /**
   * Logger Module - Provides consistent logging
   */
  const Logger = {
    prefix: '[GeoRedirect]',

    log(message) {
      console.log(`${this.prefix} ${message}`);
    },

    warn(message) {
      console.warn(`${this.prefix} ${message}`);
    }
  };

  /**
   * Geo Redirect Controller
   */
  class GeoRedirectController {
    constructor(config) {
      this.currentStoreCode = config.currentStoreCode;
      this.storeMap = config.storeMap;
      this.countryToStoreMap = config.countryToStoreMap;
      this.detectedCountry = config.detectedCountry;
      this.hasPreference = config.hasPreference || false;
      this.savedPreference = config.savedPreference || null;
    }

    async shouldSkipRedirect() {
      // Check 1: Is this a bot?
      if (BotDetector.isBot()) {
        Logger.log('Bot detected, skipping redirect');
        return true;
      }

      // Check 2: Has manual location parameter?
      if (URLManager.hasManualLocationParam()) {
        Logger.log('Manual location parameter detected, setting preference');
        await this.handleManualSelection();
        return true;
      }

      // Check 3: Has preference from Liquid?
      if (this.hasPreference && this.savedPreference) {
        Logger.log(`Stored preference found: ${this.savedPreference}, skipping redirect`);
        return true;
      }

      return false;
    }

    async handleManualSelection() {
      await PreferenceManager.set(this.currentStoreCode);
      URLManager.removeManualLocationParam();
    }

    async getTargetStore() {
      return LocationDetector.getUserCountry(this.detectedCountry)
        .then(userCountry => {
          Logger.log(`Detected country: ${userCountry}, Current store: ${this.currentStoreCode}`);

          const targetStoreCode = this.countryToStoreMap[userCountry] || userCountry;

          // Check if redirect is needed
          if (targetStoreCode === this.currentStoreCode) {
            Logger.log('User is on correct store');
            return null;
          }

          const targetStore = this.storeMap[targetStoreCode];

          if (!targetStore) {
            Logger.warn(`No store configured for code: ${targetStoreCode}`);
            return null;
          }

          return { code: targetStoreCode, ...targetStore };
        });
    }

    buildRedirectUrl(targetStore) {
      const currentPath = window.location.pathname;
      const baseUrl = `${targetStore.url}${currentPath}`;
      return URLManager.addManualLocationParam(baseUrl);
    }

    async redirect(targetStore) {
      const redirectUrl = this.buildRedirectUrl(targetStore);
      Logger.log(`Redirecting to ${targetStore.name}: ${redirectUrl}`);

      await PreferenceManager.set(targetStore.code);
      window.location.href = redirectUrl;
    }

    async execute() {
      if (await this.shouldSkipRedirect()) {
        // Set preference for current store to prevent future checks
        if (!this.hasPreference) {
          await PreferenceManager.set(this.currentStoreCode);
        }
        this.revealPage();
        return;
      }

      this.getTargetStore()
        .then(async targetStore => {
          if (targetStore) {
            Logger.log(`Country mismatch, redirecting from ${this.currentStoreCode} to ${targetStore.code}`);
            await this.redirect(targetStore);
          } else {
            // Set preference to prevent future checks
            await PreferenceManager.set(this.currentStoreCode);
            this.revealPage();
          }
        })
        .catch(async error => {
          Logger.warn(`Redirect failed: ${error.message}`);
          await PreferenceManager.set(this.currentStoreCode);
          this.revealPage();
        });
    }

    revealPage() {
      const html = document.documentElement;
      html.classList.remove('geo-redirect-checking');
      html.classList.add('geo-redirect-ready');
      Logger.log('Page revealed');
    }
  }

  /**
   * Public API
   */
  window.MyFaceDrGeoRedirect = {
    init(config) {
      const controller = new GeoRedirectController(config);

      // Execute on DOM ready
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => controller.execute());
      } else {
        controller.execute();
      }
    },

    // Expose utility for manual region switching
    addManualLocationParam(url) {
      return URLManager.addManualLocationParam(url);
    }
  };

})(window);
